import type { Option } from "@tento-lona/sheets";
import type { HeldCellStore } from "./cell-authority.ts";
import type { RowCellCache } from "./row-cell-cache.ts";

/**
 * One row cache's wiring, installed by the accessor of a sheet that holds the
 * row: the per-row fetch, the sheet-level (bulk) fetch and its coverage reset,
 * and the view of the sheet's cell store the row's cells live in (`view`,
 * `null`: the sheet keeps no cells for it). `owner` is the accessor.
 */
export interface RowFetchWiring {
  readonly owner: object;
  readonly fetchFn: RowCellCache["fetchFn"];
  readonly sheetFetch: NonNullable<RowCellCache["sheetFetch"]>;
  readonly invalidateSheetCache: NonNullable<RowCellCache["invalidateSheetCache"]>;
  view: Option<HeldCellStore>;
  /** The holder fetch installs through a captured store lease before returning wire cells. */
  installsAuthority: boolean;
}

/**
 * Told when a row cache gains its first holder (`held`) and loses its last
 * (`released`): the backend's idle-row retention (`RowCacheRetention`) keeps
 * a released row in a bounded LRU instead of forever.
 */
export interface RowHoldObserver {
  held(): void;
  released(): void;
}

/**
 * Which open sheets wire one row cache's fetch (`RowCellCache.fetchBindings`).
 * A `RowCellCache` is client-scoped (the backend's registry) and shared by
 * every open sheet holding the row; the sheet that bound it last wires it
 * (`holders`, oldest first). A closing sheet unbinds: if its wiring is the
 * active one, the newest survivor's is reinstalled, or — no sheet left — the
 * backend's own fetch (captured at the first bind) with no sheet fetch.
 * Without it the cache kept the last closed sheet's closures, and through
 * them the whole sheet, until a later sheet happened to bind the same row.
 */
export class RowFetchBindings {
  readonly #rh: RowCellCache;
  #own: Option<RowCellCache["fetchFn"]> = null;
  readonly #holders: RowFetchWiring[] = [];
  #observer: Option<RowHoldObserver> = null;

  constructor(rh: RowCellCache) {
    this.#rh = rh;
  }

  /** Observe the row's first hold and last release (`null`: stop observing). One observer: the backend's. */
  observe(observer: Option<RowHoldObserver>): void {
    this.#observer = observer;
  }

  #install(w: RowFetchWiring): void {
    this.#rh.fetchFn = w.fetchFn;
    this.#rh.fetchInstallsAuthority = w.installsAuthority;
    this.#rh.sheetFetch = w.sheetFetch;
    this.#rh.invalidateSheetCache = w.invalidateSheetCache;
    if (!this.#rh.view?.clientScoped) this.#rh.setView(w.view);
  }

  /**
   * `owner`'s store view of the row changed (its sheet now holds the row's
   * cells, or no longer does): recorded on its wiring and installed when that
   * wiring is the active one.
   */
  setView(owner: object, view: Option<HeldCellStore>): void {
    const mine = this.#holders.find((h) => h.owner === owner);
    if (mine == null) return;
    mine.view = view;
    if (this.#holders.at(-1) === mine && !this.#rh.view?.clientScoped) this.#rh.setView(view);
  }

  /** Refresh a holder's captured-install capability without changing holder order. */
  setInstallsAuthority(owner: object, installs: boolean): void {
    const mine = this.#holders.find(h => h.owner === owner);
    if (mine == null) return;
    mine.installsAuthority = installs;
    if (this.#holders.at(-1) === mine) this.#rh.fetchInstallsAuthority = installs;
  }

  /** The backend's own per-row fetch (the cache's current fetch while no sheet has bound it). */
  get own(): RowCellCache["fetchFn"] {
    return this.#own ?? this.#rh.fetchFn;
  }

  /** How many open sheets hold the row. */
  get holders(): number {
    return this.#holders.length;
  }

  /** Bind `w.owner` as the newest holder and install its wiring. */
  bind(w: RowFetchWiring): void {
    this.#own ??= this.#rh.fetchFn;
    const before = this.#holders.length;
    const at = this.#holders.findIndex((h) => h.owner === w.owner);
    if (at >= 0) this.#holders.splice(at, 1);
    this.#holders.push(w);
    this.#install(w);
    if (before === 0) this.#observer?.held();
  }

  /** Release `owner`'s hold (see the class doc). */
  unbind(owner: object): void {
    const at = this.#holders.findIndex((h) => h.owner === owner);
    if (at < 0) return;
    const mine: Option<RowFetchWiring> = this.#holders.splice(at, 1)[0] ?? null;
    if (mine != null && this.#rh.fetchFn === mine.fetchFn) { // else another holder wires it
      const survivor = this.#holders.at(-1);
      if (survivor != null) {
        this.#install(survivor);
      } else {
        this.#rh.fetchFn = this.own;
        this.#rh.fetchInstallsAuthority = false;
        this.#rh.sheetFetch = null;
        this.#rh.invalidateSheetCache = null;
        if (!this.#rh.view?.clientScoped) this.#rh.setView(null);
        this.#own = null;
      }
    }
    if (this.#holders.length === 0) this.#observer?.released();
  }
}
